/**
 * «Elektron yashil olam» vitrinasi — bosh sahifadagi blok va to'liq savdo sahifasi
 * uchun umumiy ma'lumot qatlami va mahsulot kartochkasi.
 *
 * Ko'rish uchun tizimga kirish shart emas (ochiq katalog: `GET /market/catalog/`).
 * Faqat «Sotib olish» bosilganda OneID orqali kirish so'raladi: tanlangan
 * mahsulot saqlanadi (`shared/lib/pendingPurchase.ts`) va kirishdan keyin savatga
 * tushib, xarid rasmiylashtirish mijoz kabinetida davom etadi.
 */
import { useEffect, useMemo, useState } from "react";
import type { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";

import { shopApi } from "@/shared/api/clientArea";
import type { CatalogItem, PublicService } from "@/shared/api/clientArea";
import { useI18n } from "@/shared/i18n";
import { formatMoney, formatNumber } from "@/shared/lib/format";
import { useHoverCycle } from "@/shared/lib/hoverCycle";
import { useFavorites } from "@/shared/lib/shopStore";
import { SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { SlideTrack } from "@/shared/ui/ImageGallery";
import { Icon } from "@/shared/ui/Icon";
import type { IconName } from "@/shared/ui/Icon";

import { stagger } from "./helpers";

export interface MarketItem {
  key: string;
  code: string;
  name: string;
  typeCode: string;
  typeName: string;
  categoryCode: string;
  categoryName: string;
  price: number;
  unit: string;
  seller: string;
  region: string;
  image: string;
  /** Barcha rasmlar (kartochkada ketma-ket ko'riladi). */
  images: string[];
  description: string;
  specs: Record<string, unknown>;
  available: number;
  minOrder: number;
  certificates: string[];
  /** Aksiya: eski narx (`specifications.old_price`) joriy narxdan yuqori bo'lsa. */
  oldPrice: number;
  /** Namunaviy (katalog bo'sh yoki server javob bermagan paytda ko'rsatiladi). */
  sample: boolean;
}

/** Katalog kodlari (`catalog.ProductType`) o'qiladigan nom bermaganda ko'rsatiladigan nomlar. */
const TYPE_NAMES: Record<string, string> = {
  tree: "Daraxt",
  seedling: "Ko'chat",
  sapling: "Ko'chat",
  plant: "O'simlik",
  product: "Mahsulot",
  animal: "Hayvon",
  seed: "Urug'",
};

function typeName(code: string, name?: string): string {
  return name || TYPE_NAMES[code.toLowerCase()] || code;
}

function oldPriceOf(item: CatalogItem): number {
  const old = Number((item.specifications ?? {}).old_price);
  return old > Number(item.price) ? old : 0;
}

function fromCatalog(item: CatalogItem): MarketItem {
  return {
    key: item.id,
    code: item.code,
    name: item.name,
    typeCode: item.product_type,
    typeName: typeName(item.product_type, item.product_type_name),
    categoryCode: item.category ?? "",
    categoryName: item.category_name || item.category || "",
    price: Number(item.price) || 0,
    unit: item.unit,
    seller: item.seller_name || item.seller,
    region: item.region_name || item.seller_region,
    image: item.images?.[0] ?? "",
    images: item.images ?? [],
    description: item.description ?? "",
    specs: item.specifications ?? {},
    available: Number(item.available) || 0,
    minOrder: Number(item.min_order) || 1,
    certificates: item.certificates ?? [],
    oldPrice: oldPriceOf(item),
    sample: false,
  };
}

function sample(
  n: number,
  name: string,
  typeCode: string,
  price: number,
  unit: string,
  seller: string,
  region: string,
): MarketItem {
  return {
    key: `sample-${n}`,
    code: `NAMUNA-${n}`,
    name,
    typeCode,
    typeName: typeName(typeCode),
    categoryCode: "",
    categoryName: "",
    price,
    unit,
    seller,
    region,
    image: "",
    images: [],
    description: "",
    specs: {},
    available: 0,
    minOrder: 1,
    certificates: [],
    oldPrice: 0,
    sample: true,
  };
}

const SAMPLES: MarketItem[] = [
  sample(1, "Qarag'ay ko'chati, 2 yoshli", "seedling", 4500, "dona", "Ko'chatxona №1", "Toshkent viloyati"),
  sample(2, "Yong'oq ko'chati, payvandlangan", "seedling", 18000, "dona", "Ko'chatxona №2", "Jizzax viloyati"),
  sample(3, "Archa (Juniperus) ko'chati", "seedling", 9500, "dona", "Ko'chatxona №3", "Samarqand viloyati"),
  sample(4, "Pista ko'chati, 1 yoshli", "seedling", 12000, "dona", "Ko'chatxona №4", "Surxondaryo viloyati"),
  sample(5, "Saksovul urug'i", "seed", 85000, "kg", "O'rmon xo'jaligi №1", "Qoraqalpog'iston Respublikasi"),
  sample(6, "Tol (Salix) qalamchasi", "seedling", 1800, "dona", "Ko'chatxona №5", "Xorazm viloyati"),
  sample(7, "O'rmon asali", "product", 120000, "kg", "O'rmon xo'jaligi №2", "Namangan viloyati"),
  sample(8, "Quruq o'tin (to'planma)", "product", 350000, "m³", "O'rmon xo'jaligi №3", "Farg'ona viloyati"),
];

/** Ochiq katalog; bo'sh yoki ulanmasa — namunaviy mahsulotlar (ular alohida belgilanadi). */
export function useMarketCatalog(pageSize: number) {
  const { lang } = useI18n();
  const [state, setState] = useState<{ items: MarketItem[]; loading: boolean; sample: boolean }>({
    items: [],
    loading: true,
    sample: false,
  });

  useEffect(() => {
    let cancelled = false;
    // Server bir so'rovda 100 tadan ko'p qaytarmaydi — qolgan sahifalar ketma-ket olinadi.
    const step = Math.min(pageSize, 100);
    const loadAll = async () => {
      const first = await shopApi.catalog({ page_size: step, page: 1, lang });
      const rows = [...(first.results ?? [])];
      for (let page = 2; rows.length < Math.min(first.count ?? 0, pageSize) && page <= 20; page += 1) {
        const next = await shopApi.catalog({ page_size: step, page, lang });
        if (!next.results?.length) break;
        rows.push(...next.results);
      }
      return rows;
    };
    loadAll()
      .then((rows) => {
        if (cancelled) return;
        const items = rows.map(fromCatalog);
        setState(
          items.length
            ? { items, loading: false, sample: false }
            : { items: SAMPLES, loading: false, sample: true },
        );
      })
      .catch(() => {
        if (!cancelled) setState({ items: SAMPLES, loading: false, sample: true });
      });
    return () => {
      cancelled = true;
    };
  }, [pageSize, lang]);

  return state;
}

/** Ochiq xizmatlar ro'yxati («Xizmatlar» toifasi). */
export function useMarketServices() {
  const { lang } = useI18n();
  const [state, setState] = useState<{ items: PublicService[]; loading: boolean }>({ items: [], loading: true });
  useEffect(() => {
    let cancelled = false;
    shopApi
      .publicServices(lang)
      .then((data) => !cancelled && setState({ items: data.results ?? [], loading: false }))
      .catch(() => !cancelled && setState({ items: [], loading: false }));
    return () => {
      cancelled = true;
    };
  }, [lang]);
  return state;
}

/** Mahsulot turlari ro'yxati (saralash tugmalari uchun), takrorsiz. */
export function useTypeOptions(items: MarketItem[]) {
  return useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of items) if (!seen.has(item.typeCode)) seen.set(item.typeCode, item.typeName);
    return [...seen].map(([code, name]) => ({ code, name }));
  }, [items]);
}

/** Tanlangan tur ichidagi toifalar (bitta bo'lsa yoki tur tanlanmagan bo'lsa — bo'sh). */
export function useCategoryOptions(items: MarketItem[], type: string) {
  return useMemo(() => {
    if (!type) return [];
    const seen = new Map<string, string>();
    for (const item of items) {
      if (item.typeCode === type && item.categoryCode && !seen.has(item.categoryCode)) {
        seen.set(item.categoryCode, item.categoryName);
      }
    }
    return seen.size > 1 ? [...seen].map(([code, name]) => ({ code, name })) : [];
  }, [items, type]);
}

/* ------------------------------------------------------------ xarid boshlash */

/** Mahsulot sahifasi manzili. */
export function productPath(item: Pick<MarketItem, "code">) {
  return `${SECTION_PATHS.market}/${encodeURIComponent(item.code)}`;
}

/**
 * «Sotib olish» — mahsulot sahifasiga olib boradi: u yerda miqdor kiritiladi,
 * savatga qo'shiladi yoki darhol rasmiylashtiriladi (kirish faqat rasmiylashtirishda so'raladi).
 */
export function useBuy() {
  const navigate = useNavigate();
  return (item: MarketItem) => navigate(productPath(item));
}

/** Qoldiq yozuvi: «Mavjud: 120 dona» yoki «Tugagan»; oz qolganda ogohlantiradi. */
export function stockLabel(item: MarketItem, locale: string) {
  if (item.available <= 0) return { text: "Tugagan", tone: "out" as const };
  const text = `Mavjud: ${formatNumber(item.available, locale)} ${item.unit}`;
  return { text, tone: item.available <= item.minOrder * 5 ? ("low" as const) : ("in" as const) };
}

/* ----------------------------------------------------------------- kartochka */

/** Rasm bo'lmaganda muqova: tur bo'yicha ikonka va rang (daraxt/ko'chat — yashil, urug' — amber, mahsulot — ko'k-yashil). */
function coverStyle(item: MarketItem): { icon: IconName; tone: number } {
  const key = `${item.typeCode} ${item.typeName}`.toLowerCase();
  if (/seedling|sapling|ko'chat/.test(key)) return { icon: "leaf", tone: 0 };
  if (/tree|daraxt/.test(key)) return { icon: "tree", tone: 0 };
  if (/seed|urug/.test(key)) return { icon: "sparkles", tone: 3 };
  if (/plant|o'simlik/.test(key)) return { icon: "leaf", tone: 0 };
  if (/product|mahsulot/.test(key)) return { icon: "package", tone: 4 };
  let hash = 0;
  for (const char of item.typeCode) hash = (hash * 31 + char.charCodeAt(0)) % 5;
  return { icon: "package", tone: hash };
}

export function MarketCard({
  item,
  index,
  onBuy,
}: {
  item: MarketItem;
  index: number;
  onBuy: (item: MarketItem) => void;
}) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const favorites = useFavorites();
  const cover = coverStyle(item);
  const pics = item.images.length ? item.images : item.image ? [item.image] : [];
  const { index: pic, setIndex: setPic, hoverProps } = useHoverCycle(pics);
  const current = pics[pic] ?? "";
  const go = (step: number) => setPic((value) => (value + step + pics.length) % pics.length);
  const stock = stockLabel(item, locale);
  const faved = favorites.has(item.code);
  const open = () => {
    if (!item.sample) navigate(productPath(item));
  };
  const stop = (event: MouseEvent, action: () => void) => {
    event.stopPropagation();
    action();
  };
  return (
    <article
      className={`market-card${item.sample ? "" : " is-link"}`}
      data-reveal
      style={stagger(index % 4)}
      onClick={open}
    >
      <div
        {...hoverProps}
        className={`market-cover tone-${cover.tone}${current ? " has-image" : ""}`}
      >
        {current ? <SlideTrack images={pics} index={pic} alt={item.name} /> : <Icon name={cover.icon} size={46} strokeWidth={1.3} />}
        {pics.length > 1 ? (
          <>
            <button type="button" className="market-nav is-prev" aria-label="‹" onClick={(e) => stop(e, () => go(-1))}>
              ‹
            </button>
            <button type="button" className="market-nav is-next" aria-label="›" onClick={(e) => stop(e, () => go(1))}>
              ›
            </button>
            <span className="market-dots" aria-hidden="true">
              {pic + 1} / {pics.length}
            </span>
          </>
        ) : null}
        <span className="badge market-type">{item.typeName}</span>
        {item.sample ? null : (
          <button
            type="button"
            className={`market-fav${faved ? " is-on" : ""}`}
            aria-pressed={faved}
            aria-label={faved ? "Sevimlilardan olib tashlash" : "Sevimlilarga qo'shish"}
            title={faved ? "Sevimlilardan olib tashlash" : "Sevimlilarga qo'shish"}
            onClick={(e) => stop(e, () => favorites.toggle(item.code))}
          >
            <Icon name="heart" size={18} />
          </button>
        )}
        {item.oldPrice ? <span className="market-promo">Aksiya</span> : null}
        {item.sample ? <span className="market-sample">{t("market.sample")}</span> : null}
      </div>
      <div className="market-body">
        <h3 title={item.name}>{item.name}</h3>
        <p className="market-price">
          <b className="num">{formatMoney(item.price, locale)}</b>
          <span>
            {t("market.perUnit")} {item.unit}
          </span>
          {item.oldPrice ? <s className="market-old">{formatMoney(item.oldPrice, locale)}</s> : null}
        </p>
        {item.sample ? null : <p className={`market-stock is-${stock.tone}`}>{stock.text}</p>}
        <dl className="market-meta">
          <div>
            <dt>
              <Icon name="building" size={14} />
              <span className="visually-hidden">{t("market.seller")}</span>
            </dt>
            <dd>{item.seller}</dd>
          </div>
          <div>
            <dt>
              <Icon name="pin" size={14} />
              <span className="visually-hidden">{t("market.region")}</span>
            </dt>
            <dd>{item.region || "—"}</dd>
          </div>
        </dl>
        <div className="market-actions">
          <button
            type="button"
            className="btn btn-primary btn-sm market-buy"
            onClick={(e) => stop(e, () => onBuy(item))}
          >
            <Icon name="cart" size={16} />
            {t("market.buy")}
          </button>
        </div>
      </div>
    </article>
  );
}

/** Tur bo'yicha saralash tugmalari. */
export function TypeFilter({
  options,
  value,
  onChange,
}: {
  options: Array<{ code: string; name: string }>;
  value: string;
  onChange: (code: string) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="chip-row" role="group" aria-label={t("market.filterLabel")}>
      {[{ code: "", name: t("market.filterAll") }, ...options].map((option) => (
        <button
          key={option.code || "all"}
          type="button"
          className={`chip${value === option.code ? " is-on" : ""}`}
          aria-pressed={value === option.code}
          onClick={() => onChange(option.code)}
        >
          {option.name}
        </button>
      ))}
    </div>
  );
}
